import { Component, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { ProviderNoteDetailService } from 'src/app/services/shopping/provider-note-detail.service';
import { ProviderNoteService } from 'src/app/services/shopping/provider-note.service';

@Component({
  selector: 'app-provider-note',
  templateUrl: './provider-note.component.html',
  styleUrls: ['./provider-note.component.scss'],
  standalone: false
})
export class ProviderNoteComponent implements OnInit {

  private readonly headerService = inject(HeaderService);
  private readonly providerNoteDetailService = inject(ProviderNoteDetailService);
  private readonly providerNoteService = inject(ProviderNoteService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<any> = new PagedResponseModel<any>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<any> = new PagedResponseModel<any>();
  generalHeader: ColumnResponseModel[] = [];
  limit: number = 10;
  nameWindow: string = 'Notas Proveedores';
  page: number = 1;
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    },
  ];

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem('provider-note');
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getProviderNotesForGeneralConsultation(),
    ]);
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.providerNoteDetailService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Notas Proveedores Detalles.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de las notas proveedores.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.providerNoteService.exportGeneralConsultation(
        this.filters, this.columnSort, this.ascendant
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Notas Proveedores.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las notas proveedores.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.providerNoteDetailService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta de detalle.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.providerNoteService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getProviderNoteDetailsForGeneralConsultation() {
    try {
      this.detailData = await firstValueFrom(this.providerNoteDetailService.getProviderNoteDetailsForGeneralConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar el detalle de las notas de proveedores.', error);
    }
  }

  async getProviderNotesForGeneralConsultation() {
    try {
      this.generalData = await firstValueFrom(this.providerNoteService.getProviderNotesForGeneralConsultation(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar las notas de proveedores.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getProviderNotesForGeneralConsultation()
        ]);
        this.generalData = { ...this.generalData, currentPage: 1, pageSize: 10 };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getProviderNoteDetailsForGeneralConsultation()
        ]);
        this.detailData = { ...this.detailData, currentPage: 1, pageSize: 10 };
        break;
    }
  }

  newProviderNote() {
    this.router.navigate(['/provider-note/register']);
  }

  onAction(event: { action: string, item?: any, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/provider-note/register/${event.item.providerNoteId}`]);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getProviderNotesForGeneralConsultation();
        break;
      case 1:
        await this.getProviderNoteDetailsForGeneralConsultation();
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

}
